import React, { FC, MouseEvent } from "react";
import { Box, Typography } from "@mui/material";
import styled from "@emotion/styled";
import { Album } from "@models/album";
import PhotoPlaceholder from "@components/PhotoPlaceholder";
import { useTranslation } from "react-i18next";
import { DateUtil } from "@utils/date.util";
import { Link, useNavigate } from "react-router-dom";
import TextIconButton from "@components/TextIconButton";
import { Mode } from "@store/homeSlice";
import { MmsOutlined } from '@mui/icons-material';
import { ActivityType } from '@models/activity-type';

const Container = styled.div`
  border-radius: 4px;
  border-width: 1px;
  border-color: rgb(189, 193, 202, 0.4);
  border-style: solid;
  padding: 10px 20px 16px;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const StyledCheckbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const Image = styled.img`
  width: auto;
  height: 166px;
  object-fit: cover;
  border-radius: 4px;
`;

const AlbumItem: FC<{
  album: Album;
  onCheck?: () => void;
  checked?: boolean;
  mode?: Mode;
}> = ({ album, onCheck, checked, mode = Mode.Caregiver }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const navigateToCompanion = (event: MouseEvent) => {
    event.stopPropagation();
    navigate(`/portal/talk`, { state: { albumId: album.id, activityType: ActivityType.GallerySession, isPatient: true } });
  };

  return (
    <Container>
      <Box display="flex" flexDirection="column" gap="16px">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap="16px"
        >
          <Typography
            color="textSecondary"
            fontSize="20px"
            fontWeight="500"
            lineHeight="30px"
            marginBottom="6px"
            noWrap={true}
            sx={{ textDecoration: "none" }}
            {...(mode === Mode.Caregiver
              ? {
                  component: Link,
                  to: album.id,
                }
              : null)}
          >
            { album.allPhotos ? t('albums.allPhotosAlbumLabel') : album.name }
          </Typography>
          {onCheck && !album.allPhotos && (
            <StyledCheckbox
              type="checkbox"
              checked={checked}
              onChange={onCheck}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </Box>
        {album.coverPhoto ? (
          <Image src={album.coverPhoto.fileUrl} alt={album.name} />
        ) : (
          <PhotoPlaceholder width="100%" height="auto" />
        )}

        <Box
          display="flex"
          justifyContent="space-between"
          fontSize="14px"
          lineHeight="22px"
          fontWeight="400"
          color="#171A1FFF"
        >
          <span>
            {t("dashboard.albums.numberOfPhotos", {
              photos: album.photoCount || 0,
            })}
          </span>
          <span>{DateUtil.formatTime(album.lastUpdatedDate)}</span>
        </Box>

        {mode === Mode.Patient && (
          <Box display="flex" justifyContent="center" marginTop="24px">
            <TextIconButton
              onClick={navigateToCompanion}
              variant="contained"
              color="success"
              size="large"
              iconLeft={true}
              text={t("patientDashboard.albums.watchButton")}
              icon={MmsOutlined}
            />
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default AlbumItem;
